import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Silhuetas náuticas por categoria (24x24, monocromáticas via currentColor).
 * Tingíveis: defina a cor com `style={{ color }}` ou classe de texto.
 * Detalhes em traço usam stroke="currentColor"; silhuetas usam fill.
 */
const S = { fill: 'currentColor' } as const;
const L = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const PATHS: Record<string, ReactNode> = {
  // Barco genérico — casco + cabine
  barco: (<><path {...S} d="M2.5 14h19l-2.2 4.4a1 1 0 0 1-.9.6H5.6a1 1 0 0 1-.9-.6z" /><path {...S} d="M8.5 7.5h7l-.7 6h-5.6z" /></>),
  // Casco (apenas o casco, curvo)
  casco: (<path {...S} d="M2 12.5c3.6 3.2 16.4 3.2 20 0l-2.3 6.1a1 1 0 0 1-.9.4H5.2a1 1 0 0 1-.9-.4z" />),
  // BassBoat — casco baixo e largo + console
  bassboat: (<><path {...S} d="M1.5 15h21l-1.4 3.2a1 1 0 0 1-.9.6H3.8a1 1 0 0 1-.9-.6z" /><path {...S} d="M10 11.5h4v3.5h-4z" /></>),
  // Bote inflável — casco arredondado
  bote: (<path {...S} d="M3 12a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1c0 4.6-3.9 7-9 7s-9-2.4-9-7z" />),
  // Apoio — rebocador (cabine alta + chaminé)
  apoio: (<><path {...S} d="M2 15h20l-2.1 4.4a1 1 0 0 1-.9.6H5a1 1 0 0 1-.9-.6z" /><path {...S} d="M5 7h6v8H5z" /><path {...S} d="M12 9.5h2.5V15H12z" /></>),
  // Canoa — casco aberto (crescente)
  canoa: (<path {...S} d="M3 11c3 3 15 3 18 0-1 5-5 7-9 7s-8-2-9-7z" />),
  // Caiaque — casco fino aberto + remo
  caiaque: (<><path {...S} d="M3 11.5c3 2 15 2 18 0-1 4-5 5.5-9 5.5s-8-1.5-9-5.5z" /><path {...L} d="M6 6l12 11" /></>),
  // Lancha — casco esportivo + para-brisa
  lancha: (<><path {...S} d="M2 14.5h19.5l-2 3.6a1 1 0 0 1-.9.5H4.6a1 1 0 0 1-.9-.5z" /><path {...S} d="M9 9.5h7l1 4.5H9z" /></>),
  // Reboque — barco sobre carreta com rodas
  reboque: (<><path {...S} d="M4 8.5h15l-2.2 4.2a1 1 0 0 1-.9.5H7a1 1 0 0 1-.9-.5z" /><path {...L} d="M5 14.5h14" /><circle {...S} cx="8" cy="17.5" r="1.8" /><circle {...S} cx="16" cy="17.5" r="1.8" /></>),
  // Veleiro — casco + mastro + vela triangular
  veleiro: (<><path {...S} d="M4.5 16h13l-1.4 3a1 1 0 0 1-.9.5H6.8a1 1 0 0 1-.9-.5z" /><path {...L} d="M11.5 3.5V15" /><path {...S} d="M11.5 4.5l5 9.5h-5z" /></>),
  // Catamarã — dois cascos + convés + vela
  catamara: (<><path {...S} d="M2.5 16h6l-1 2.6a1 1 0 0 1-.9.4H3.4a1 1 0 0 1-.9-.4z" /><path {...S} d="M15.5 16h6l-1 2.6a1 1 0 0 1-.9.4h-3.2a1 1 0 0 1-.9-.4z" /><path {...S} d="M4 14h16v2H4z" /><path {...S} d="M12 5l3.2 8H12z" /></>),
  // Iate — múltiplos conveses
  iate: (<><path {...S} d="M2 15h20l-2 4H4z" /><path {...S} d="M5 10.5h13v4.5H5z" /><path {...S} d="M8 7.5h7v3H8z" /></>),
  // Escuna — dois mastros + velas
  escuna: (<><path {...S} d="M3 16h16l-1.5 3a1 1 0 0 1-.9.5H5.4a1 1 0 0 1-.9-.5z" /><path {...L} d="M8 4v11M15.5 4v11" /><path {...S} d="M8 5l3 9.5H8z" /><path {...S} d="M15.5 5l3 9.5h-3z" /></>),
  // Ferry boat — navio caixa com ponte
  ferryboat: (<><path {...S} d="M2 15h20l-1.6 4a1 1 0 0 1-.9.6H4.5a1 1 0 0 1-.9-.6z" /><path {...S} d="M4 8.5h16V15H4z" /><path {...S} d="M9 5h6v3.5H9z" /><path {...L} d="M6 11.5h2.5M11 11.5h2.5M16 11.5h2" /></>),
  // Balsa — convés plano + rampa + carga
  balsa: (<><path {...S} d="M2 13h19.5l-1 4a1 1 0 0 1-.9.7H3.9a1 1 0 0 1-.9-.7z" /><path {...S} d="M8 9h8v4H8z" /></>),
  // Resgate — casco + boia salva-vidas
  resgate: (<><path {...S} d="M2.5 16h19l-2 3.4a1 1 0 0 1-.9.6H5.4a1 1 0 0 1-.9-.6z" /><circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="2.4" /></>),
  // Plataforma — convés sobre pernas + torre
  plataforma: (<><path {...S} d="M3 11h18v2H3z" /><path {...L} d="M5.5 13v6M12 13v6M18.5 13v6" /><path {...S} d="M9 3l3 8H9z" /><path {...S} d="M12 3l3 8h-3z" /></>),
  // Flutuante — plataforma sobre dois flutuadores
  flutuante: (<><path {...S} d="M3 10.5h18v3H3z" /><path {...S} d="M4 14.5h6v2.5H4z" /><path {...S} d="M14 14.5h6v2.5h-6z" /><path {...S} d="M11 5.5h2v5h-2z" /></>),
  // Jet ski — moto aquática (perfil) + guidão
  jetski: (<><path {...S} d="M3 15c2-2.6 6-3.6 10-3.6 5 0 8.5 1.6 8.5 3.6 0 1.3-1.2 2.4-3.2 2.4H6.2C4.2 17.4 3 16.3 3 15z" /><path {...S} d="M10 8.5h4l1 3.4h-6z" /><path {...L} d="M14.5 9l3-2.2" /></>),
};

export const VESSEL_ICON_KEYS = Object.keys(PATHS);

export function VesselIcon({
  category,
  className,
  color,
  title,
}: {
  category: string | null | undefined;
  className?: string;
  color?: string;
  title?: string;
}) {
  const key = (category ?? '').toLowerCase();
  const inner = PATHS[key] ?? PATHS.barco;
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn('shrink-0', className)}
      style={color ? { color } : undefined}
      role="img"
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      {inner}
    </svg>
  );
}
